import type { AvailableExtraCosts, CalculateExtraCostsPriceBreakdownView } from "@slfinrtl/ui-bff-routes"
import { calculateExtraCostsPriceBreakdownRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import type { UseFormSetValue } from "react-hook-form"
import type { CalculatedPriceBreakdownView, InventoryInformationFormValues } from "src/components"
import { useBffDynamic, useJsonMemorizedValue, useRequestQueueWrapper } from "src/hooks"

const MAX_RETRIES = 5

export const useCalculatedPriceBreakdown = (inventoryId: string) => {
	const [calculatedPriceBreakdownView, setCalculatedPriceBreakdownView] = useState<CalculatedPriceBreakdownView>({
		state: "loading",
	})
	const { setValue, watch } = useFormContext<InventoryInformationFormValues>()
	const shouldUseInventoryPriceService = useMemo(() => {
		return new URLSearchParams(window.location.search).get("useInventoryPriceService") === "true" ? true : undefined
	}, [])
	const retries = useRef(0)

	const calculateExtraCostsPriceBreakdown = useRequestQueueWrapper(
		useBffDynamic(calculateExtraCostsPriceBreakdownRoute, "inline"),
		undefined,
	)

	const vehicleType = watch("extraCostsVehicleType")
	const vehiclePrice = watch("vehiclePrice")
	const selectedExtraCostIds = useJsonMemorizedValue(watch("selectedExtraCostIds"))

	const fetchExtraCostsPriceBreakdownRef = useRef<(() => Promise<void>) | null>(null)

	const fetchExtraCostsPriceBreakdown = useCallback(async () => {
		let previouslyAvailableExtraCostIds: string[] = []
		setCalculatedPriceBreakdownView((prev) => {
			previouslyAvailableExtraCostIds =
				prev.state !== "error" ? extractExtraCostIds(prev.view?.availableExtraCosts) : []
			return { state: "loading", view: prev.state !== "error" ? prev.view : undefined }
		})

		try {
			const newCalculatedPriceBreakdown = await calculateExtraCostsPriceBreakdown({
				body: {
					selectedExtraCostIds,
					vatDeductible: true, // doesn't affect calculation, but is required in the input model. Using the real value results in unnecessary recalculations.
					vehiclePrice,
				},
				pathParams: {
					inventoryId,
				},
				queryParams: {
					useInventoryPriceService: shouldUseInventoryPriceService,
				},
			})

			// Retry fetching the extra costs if no categories are available. This can happen when the vehicle is
			// initially added to the inventory. Then the extra costs are not yet available and we need to wait a bit.
			if (
				(!newCalculatedPriceBreakdown?.availableExtraCosts?.categories ||
					newCalculatedPriceBreakdown.availableExtraCosts.categories.length === 0) &&
				retries.current < MAX_RETRIES
			) {
				retries.current += 1
				setTimeout(() => {
					fetchExtraCostsPriceBreakdownRef.current?.()
				}, 1000)
				return
			}

			updateFormValueIfNecessary(
				newCalculatedPriceBreakdown,
				previouslyAvailableExtraCostIds,
				selectedExtraCostIds,
				setValue,
			)

			setCalculatedPriceBreakdownView({
				state: "complete",
				view: newCalculatedPriceBreakdown,
			})
		} catch {
			setCalculatedPriceBreakdownView({ state: "error" })
		}
	}, [
		calculateExtraCostsPriceBreakdown,
		inventoryId,
		selectedExtraCostIds,
		setValue,
		vehiclePrice,
		shouldUseInventoryPriceService,
	])

	useEffect(() => {
		fetchExtraCostsPriceBreakdownRef.current = fetchExtraCostsPriceBreakdown
	}, [fetchExtraCostsPriceBreakdown])

	useEffect(() => {
		fetchExtraCostsPriceBreakdownRef.current?.()
	}, [fetchExtraCostsPriceBreakdown, vehicleType])

	return calculatedPriceBreakdownView
}

const updateFormValueIfNecessary = (
	newCalculatedPriceBreakdown: CalculateExtraCostsPriceBreakdownView | undefined,
	previouslyAvailableExtraCostIds: string[],
	previouslySelectedExtraCostIds: string[],
	setValue: UseFormSetValue<InventoryInformationFormValues>,
) => {
	const newAvailableExtraCostIds = extractExtraCostIds(newCalculatedPriceBreakdown?.availableExtraCosts)
	const shouldApplyPersistedExtraCostIds = !idsMatch(previouslyAvailableExtraCostIds, newAvailableExtraCostIds)
	const unfilteredSelection = shouldApplyPersistedExtraCostIds
		? (newCalculatedPriceBreakdown?.persistedSelectedExtraCostIds ?? [])
		: previouslySelectedExtraCostIds
	const filteredSelection = unfilteredSelection.filter((id) => newAvailableExtraCostIds.includes(id))
	if (!idsMatch(filteredSelection, previouslySelectedExtraCostIds)) {
		setValue("selectedExtraCostIds", filteredSelection)
	}
}

const extractExtraCostIds = (extraCosts?: AvailableExtraCosts) =>
	extraCosts?.categories.flatMap((category) => category.extraCosts.map((extraCost) => extraCost.id)) ?? []

const idsMatch = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id))
